/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { StyledBreadCrumbContainer, StyledLink } from "../../components/navigable_breadcrumb_link/styles";
import {
  Box,
  Breadcrumbs,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Button,
  Tooltip,
  Typography,
} from "@mui/material";
import convertBase64 from "../../utils/convertBase64";
import CloseIcon from "@mui/icons-material/Close";
import {
  RestaurantGroupContainer,
  StyledTitleContainer,
  StyledTitle,
  StyledCreateButton,
  StyledButtonContainer,
  StyledCancelButton,
  StyledAddButton,
  StyledCreateTitle,
  StyledFormContainer,
  StyledFormFieldText,
  StyledTextField,
  StyledLogoSection,
  StyledLogoSectionHeader,
  StyledLogoSectionSubheader,
  StyledLogoGrid,
  StyledLogoCard,
  StyledLogoPreviewFrame,
  StyledLogoPreview,
  StyledRequirementList,
  StyledRequirementItem,
  StyledLogoActions,
  StyledSharedRequirements,
  StyledGoldBackgroundSection,
  StyledColorChipGrid,
  StyledColorChip,
  StyledColorChipLabel,
  StyledColorEditorRow,
  StyledColorChipWrapper,
  StyledColorChipDeleteButton,
} from "./styles";
import { AbilityContext } from "../../contexts/abilityContext";
import AddIcon from '@mui/icons-material/Add';
import { LoadingSpinner } from "../../components/loading_spinner";
import api from "../../api";
import authenticatedFetch from "../../api/authenticatedFetch";
import { useNotificationContext } from '../../contexts/notificationContext'
import { RestaurantGroupTable } from "../../components/restaurant_group_table";
import { strictCanScope } from "../../utils/caslStrict";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader } from "../../components/loader";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { ConfirmationDialog } from "../../components/confirmation_dialog";
import { formatDate } from "../../utils/formatDate";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { checkAdmin } from "../../utils/checkAdmin";
import { Query_Keys } from "../../lib/queryKeys";
import { useOtMutation } from "../../hooks/mutations/useOtMutation";
import { restaurantGroupMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/restaurantGroupMutationKeys";
import { resetPageAfterDeletion } from '../../utils/resetPageAfterDeletion'
import { RenderRestaurantSubGroups } from "../../components/render_restaurant_sub_groups";
import { RenderRestaurantPartners } from "../../components/render_restaurant_partners";
import { CreateRestaurantSubGroupModal, EditRestaurantSubGroupModal } from "./create_sub_group";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyledDatePicker } from "../network_guests/styles";
import { CreatePartnershipModal, EditPartnershipModal } from "./partnership_modal";
import HandshakeIcon from '@mui/icons-material/Handshake';

const LOGO_FIELDS = [
  {
    key: 'wallet_dark_logo',
    label: 'Light Background Logo',
    description: 'Use this for light colored background cards.',
    previewBackground: '#ffffff',
  },
  {
    key: 'wallet_light_logo',
    label: 'Dark Background Logo',
    description: 'Use this for dark colored background cards',
    previewBackground: '#000000',
  },
  {
    key: 'wallet_gold_logo',
    label: 'Gold Logo',
    description: 'Use this for premium or selected colored background cards.',
    previewBackground: '#b08d57',
  },
]

// The in-app discount card logo. It follows the same rules as the wallet logos but is not a
// wallet asset, so it renders in its own section.
const DISPLAY_PHOTO_FIELD = {
  key: 'display_profile_photo',
  label: 'Display Profile Photo',
  description: 'Use this for Discount Card Logo. Required size: 512 x 512 pixels.',
  previewBackground: '#f8fafc',
  placeholder: 'https://placehold.co/512x512/?text=No+logo+uploaded',
}

const PLACEHOLDER_LOGO = 'https://placehold.co/1125x369/?text=No+logo+uploaded'
const LOGO_MAX_SIZE_BYTES = 1 * 1024 * 1024
const LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg']
const LOGO_REQUIRED_WIDTH = 512
const LOGO_REQUIRED_HEIGHT = 512
const DEFAULT_GOLD_LOGO_BACKGROUNDS = ['#FFFFFF', '#000000']
const GOLD_BACKGROUND_LIMIT = 100
const HEX_COLOR_REGEX = /^#([0-9A-F]{6})$/i

const createLogoState = (url = '') => ({
  url,
  data: null,
  error: '',
})

const buildInitialLogoState = (record = {}) => ({
  display_profile_photo: createLogoState(record?.display_profile_photo?.url || ''),
  wallet_light_logo: createLogoState(record?.wallet_light_logo?.url || ''),
  wallet_dark_logo: createLogoState(record?.wallet_dark_logo?.url || ''),
  wallet_gold_logo: createLogoState(record?.wallet_gold_logo?.url || ''),
})

const normalizeHexColor = (value = '') => {
  const trimmedValue = value.trim().toUpperCase()
  if (!trimmedValue) {
    return ''
  }

  return trimmedValue.startsWith('#') ? trimmedValue : `#${trimmedValue}`
}

const CUT_OFF_DATE_FORMAT = 'YYYY-MM-DD'

const parseCutOffDate = (value) => (value ? dayjs(value) : null)

const isInvalidCutOffDate = (value) => Boolean(value) && !value.isValid()

const formatCutOffDate = (value) => (value?.isValid() ? value.format(CUT_OFF_DATE_FORMAT) : null)

const CutOffDateField = ({ value, onChange, error, testId }) => (
  <div style={{ marginTop: '3%' }}>
    <StyledFormFieldText>Cutoff Date</StyledFormFieldText>
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <StyledDatePicker
        value={value}
        onChange={onChange}
        slotProps={{
          field: { clearable: true },
          textField: {
            inputProps: { 'data-testid': testId },
            error: Boolean(error),
            helperText: error,
          },
        }}
      />
    </LocalizationProvider>
  </div>
)

const buildInitialGoldBackgrounds = (record = {}) => {
  const existingColors = Array.isArray(record?.wallet_gold_logo_backgrounds)
    ? record.wallet_gold_logo_backgrounds.map(normalizeHexColor).filter(Boolean)
    : []

  return existingColors.length ? existingColors : DEFAULT_GOLD_LOGO_BACKGROUNDS
}

const getReadableHexLabelColors = (hexColor) => {
  const normalizedColor = normalizeHexColor(hexColor)
  const hexValue = normalizedColor.replace('#', '')
  const red = Number.parseInt(hexValue.slice(0, 2), 16)
  const green = Number.parseInt(hexValue.slice(2, 4), 16)
  const blue = Number.parseInt(hexValue.slice(4, 6), 16)
  const luminance = (0.299 * red) + (0.587 * green) + (0.114 * blue)

  return luminance > 200
    ? {
        text: '#111827',
        background: 'rgba(255, 255, 255, 0.92)',
        border: 'rgba(17, 24, 39, 0.16)',
      }
    : {
        text: '#ffffff',
        background: 'rgba(17, 24, 39, 0.82)',
        border: 'rgba(255, 255, 255, 0.22)',
      }
}

const getLogoPreviewUrl = (logoState, placeholder = PLACEHOLDER_LOGO) => logoState?.data || logoState?.url || placeholder

// Renders one logo upload card: preview, upload or replace control and its error text.
// Extra controls for a specific field are passed as children.
const LogoFieldCard = ({ field, logoState, previewBackground, onUpload, children }) => {
  const hasCustomLogo = Boolean(logoState.data || logoState.url)

  return (
    <StyledLogoCard data-testid={`${field.key}-card`}>
      <Typography sx={{ fontSize: '15px', fontWeight: 700, lineHeight: '22px' }}>
        {field.label}
      </Typography>
      <Typography sx={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
        {field.description}
      </Typography>

      <Box sx={{ marginTop: '12px' }}>
        <StyledLogoPreviewFrame
          style={{ backgroundColor: previewBackground }}
          data-testid={`${field.key}-preview-frame`}
        >
          <StyledLogoPreview
            src={getLogoPreviewUrl(logoState, field.placeholder)}
            alt={hasCustomLogo ? field.label : `${field.label} placeholder`}
            data-testid={`${field.key}-preview-image`}
          />
        </StyledLogoPreviewFrame>
      </Box>

      <StyledLogoActions>
        <Button variant="outlined" component="label" sx={{ textTransform: 'none', borderRadius: '10px' }}>
          {hasCustomLogo ? 'Replace' : 'Upload'}
          <input
            hidden
            accept=".png,.jpg,.jpeg"
            type="file"
            data-testid={`${field.key}-upload-input`}
            onChange={(event) => onUpload(event, field.key)}
          />
        </Button>
      </StyledLogoActions>

      {logoState.error ? (
        <Typography sx={{ color: '#b91c1c', fontSize: '12px', marginTop: '8px' }} data-testid={`${field.key}-error`}>
          {logoState.error}
        </Typography>
      ) : null}

      {children}
    </StyledLogoCard>
  )
}

// The logo a discount card shows in the app and on the wallet pass.
const DisplayPhotoSection = ({ logoState, onUpload }) => {
  return (
    <StyledLogoSection>
      <StyledLogoSectionHeader>Card logo</StyledLogoSectionHeader>
      <StyledLogoSectionSubheader>
        Shown on the discount card in the mobile app and on the wallet pass. PNG or JPEG, exactly
        512 x 512 pixels, up to 1 MB.
      </StyledLogoSectionSubheader>

      <StyledLogoGrid>
        <LogoFieldCard
          field={DISPLAY_PHOTO_FIELD}
          logoState={logoState}
          previewBackground={DISPLAY_PHOTO_FIELD.previewBackground}
          onUpload={onUpload}
        />
      </StyledLogoGrid>
    </StyledLogoSection>
  )
}

const LogoUploadSection = ({
  logos,
  onUpload,
  goldLogoBackgrounds,
  selectedGoldLogoBackground,
  onSelectGoldBackground,
  goldBackgroundInput,
  onGoldBackgroundInputChange,
  onGoldBackgroundAdd,
  onGoldBackgroundRemove,
  goldBackgroundError,
}) => {
  return (
    <>
      <DisplayPhotoSection
        logoState={logos[DISPLAY_PHOTO_FIELD.key] || createLogoState()}
        onUpload={onUpload}
      />

      <StyledLogoSection>
        <StyledLogoSectionHeader>Wallet logos</StyledLogoSectionHeader>
        <StyledLogoSectionSubheader>
          Upload square logo assets for the light, dark, and gold wallet logo variants.
        </StyledLogoSectionSubheader>

        <StyledSharedRequirements>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
            Shared requirements
          </Typography>
          <StyledRequirementList>
            <StyledRequirementItem>Supported formats: PNG and JPEG</StyledRequirementItem>
            <StyledRequirementItem>Required dimensions: 512 x 512 pixels</StyledRequirementItem>
            <StyledRequirementItem>Maximum file size: 1 MB</StyledRequirementItem>
            <StyledRequirementItem>If no custom logo is uploaded, the default card logo will be used</StyledRequirementItem>
          </StyledRequirementList>
        </StyledSharedRequirements>

        <StyledLogoGrid>
          {LOGO_FIELDS.map((field) => {
            const logoState = logos[field.key] || createLogoState()
            const previewBackground = field.key === 'wallet_gold_logo'
              ? (selectedGoldLogoBackground || DEFAULT_GOLD_LOGO_BACKGROUNDS[0])
              : field.previewBackground

            return (
              <LogoFieldCard
                key={field.key}
                field={field}
                logoState={logoState}
                previewBackground={previewBackground}
                onUpload={onUpload}
              >
                {field.key === 'wallet_gold_logo' ? (
                  <StyledGoldBackgroundSection>
                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>
                      Gold wallet backgrounds
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Pick the card background colors that should use the gold logo. Click a swatch to preview it live.
                    </Typography>

                    <StyledColorChipGrid data-testid="wallet-gold-background-list">
                      {goldLogoBackgrounds.map((color) => {
                        const isSelected = color === selectedGoldLogoBackground
                        const labelColors = getReadableHexLabelColors(color)
                        const canRemoveColor = goldLogoBackgrounds.length > 1

                        return (
                          <StyledColorChipWrapper key={color}>
                            <StyledColorChipDeleteButton
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation()
                                if (canRemoveColor) {
                                  onGoldBackgroundRemove(color)
                                }
                              }}
                              disabled={!canRemoveColor}
                              aria-label={`Remove background color ${color}`}
                              data-testid={`wallet-gold-background-delete-${color.slice(1)}`}
                            >
                              <DeleteIcon sx={{ fontSize: '16px', color: canRemoveColor ? '#dc2626' : '#94a3b8' }} />
                            </StyledColorChipDeleteButton>
                            <StyledColorChip
                              type="button"
                              onClick={() => onSelectGoldBackground(color)}
                              selected={isSelected ? 1 : 0}
                              colorValue={color}
                              data-testid={`wallet-gold-background-${color.slice(1)}`}
                              aria-label={`Preview gold logo on ${color}`}
                            >
                              <StyledColorChipLabel style={{ color: labelColors.text, backgroundColor: labelColors.background, border: `1px solid ${labelColors.border}` }}>
                                {color}
                              </StyledColorChipLabel>
                            </StyledColorChip>
                          </StyledColorChipWrapper>
                        )
                      })}
                    </StyledColorChipGrid>

                    <StyledColorEditorRow>
                      <Box
                        component="input"
                        type="color"
                        value={HEX_COLOR_REGEX.test(goldBackgroundInput) ? goldBackgroundInput : DEFAULT_GOLD_LOGO_BACKGROUNDS[0]}
                        onChange={(event) => onGoldBackgroundInputChange(event.target.value)}
                        sx={{ width: '46px', height: '46px', padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                        data-testid="wallet-gold-background-color-picker"
                      />
                      <StyledTextField
                        size="small"
                        value={goldBackgroundInput}
                        onChange={(event) => onGoldBackgroundInputChange(event.target.value)}
                        placeholder="#FFFFFF"
                        inputProps={{ maxLength: 7, 'data-testid': 'wallet-gold-background-input' }}
                        sx={{ width: '130px' }}
                      />
                      <Button
                        type="button"
                        variant="outlined"
                        onClick={onGoldBackgroundAdd}
                        sx={{ textTransform: 'none', borderRadius: '10px' }}
                        data-testid="wallet-gold-background-add"
                      >
                        Add color
                      </Button>
                    </StyledColorEditorRow>

                    {goldBackgroundError ? (
                      <Typography sx={{ color: '#b91c1c', fontSize: '12px', marginTop: '8px' }} data-testid="wallet-gold-background-error">
                        {goldBackgroundError}
                      </Typography>
                    ) : null}
                  </StyledGoldBackgroundSection>
                ) : null}
              </LogoFieldCard>
            )
          })}
        </StyledLogoGrid>
      </StyledLogoSection>
    </>
  )
}

export const RestaurantGroupPage = () => {
  const { ability } = React.useContext(AbilityContext)
  const showNotification = useNotificationContext()
  const [open, setOpen] = React.useState(false)
  const [status, setStatus] = React.useState(false)
  const [restaurantGroup, setRestaurantGroup] = React.useState("");
  const [restaurantGroupError, setRestaurantGroupError] = React.useState({});
  const [groupLogos, setGroupLogos] = React.useState(buildInitialLogoState())
  const [goldLogoBackgrounds, setGoldLogoBackgrounds] = React.useState(DEFAULT_GOLD_LOGO_BACKGROUNDS)
  const [selectedGoldLogoBackground, setSelectedGoldLogoBackground] = React.useState(DEFAULT_GOLD_LOGO_BACKGROUNDS[0])
  const [goldBackgroundInput, setGoldBackgroundInput] = React.useState(DEFAULT_GOLD_LOGO_BACKGROUNDS[0])
  const [goldBackgroundError, setGoldBackgroundError] = React.useState('')
  const [itemsPerPage, setItemsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(1);
  const [loadingStatus, setLoadingStatus] = React.useState(false);
  const [sortBy, setSortBy] = React.useState('created_at')
  const [sortOrder, setSortOrder] = React.useState('desc')
  const drawerWidth = 470
  const createPermission = strictCanScope(ability, 'create', 'Restaurant_Group');
  const readPermission = ability.can('read', 'Restaurant_Group');
  const deletePermission = strictCanScope(ability, 'delete', 'Restaurant_Group');
  const queryClient = useQueryClient();
  const [selectedRestaurantGroup, setSelectedRestaurantGroup] = React.useState({})
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = React.useState(false)
  const [subGroupModalOpen, setSubGroupModalOpen] = React.useState(false)
  const [editSubGroupModalOpen, setEditSubGroupModalOpen] = React.useState(false)
  const [deleteSubGroupModalOpen, setDeleteSubGroupModalOpen] = React.useState(false)
  const [selectedRestaurantSubGroup, setSelectedRestaurantSubGroup] = React.useState(null)
  const [isDeletingSubGroup, setIsDeletingSubGroup] = React.useState(false)
  const [isFetchingSubGroupDetails, setIsFetchingSubGroupDetails] = React.useState(false)
  const [restaurantGroupName, setRestaurantGroupName] = React.useState('')
  const [cutOffDate, setCutOffDate] = React.useState(null)
  const [partnershipModalOpen, setPartnershipModalOpen] = React.useState(false)
  const [partnershipRestaurantGroup, setPartnershipRestaurantGroup] = React.useState(null)
  const [selectedPartnership, setSelectedPartnership] = React.useState(null)
  const [editPartnershipModalOpen, setEditPartnershipModalOpen] = React.useState(false)
  const [deletePartnershipDialogOpen, setDeletePartnershipDialogOpen] = React.useState(false)
  const [isDeletingPartnership, setIsDeletingPartnership] = React.useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const { admin } = useSelector((state) => state.admin);
  const superAdmin = checkAdmin(admin?.formatted_roles)
  const canEditCutOffDate = superAdmin?.role === 'admin'

  const fetchRestaurantGroups = async () => {
    const response = await api.get('/v1/admin/restaurant_groups', {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: sortBy,
        sort_order: sortOrder,
      }
    })
    return { data: response?.data, pageCount: response?.headers["total-count"]}
  }

  const { data, isLoading } = useQuery({
    queryKey: Query_Keys.restaurant_groups({
      page: page,
      itemsPerPage: itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: fetchRestaurantGroups,
    staleTime: 10 * 60 * 1000,
  })

  const resetLogoState = (record = {}) => {
    setGroupLogos(buildInitialLogoState(record))
    const initialGoldBackgrounds = buildInitialGoldBackgrounds(record)
    setGoldLogoBackgrounds(initialGoldBackgrounds)
    setSelectedGoldLogoBackground(initialGoldBackgrounds[0] || DEFAULT_GOLD_LOGO_BACKGROUNDS[0])
    setGoldBackgroundInput(initialGoldBackgrounds[0] || DEFAULT_GOLD_LOGO_BACKGROUNDS[0])
    setGoldBackgroundError('')
  }

  const handleEditRestaurantGroup = (group) => {
    setRestaurantGroupError('')
    setSelectedRestaurantGroup(group)
    setRestaurantGroupName(group?.name)
    setCutOffDate(parseCutOffDate(group?.cut_off_date))
    resetLogoState(group)
    setEditDrawerOpen(true)
  }

  const { mutateAsync: fetchRestaurantSubGroupDetailsMutation } = useOtMutation({
    mutationFn: async ({ subGroupId }) => await api.get(`/v1/admin/restaurant_sub_groups/${subGroupId}`),
    errorMessage: () => null,
    onSuccess: (response, variables) => {
      const restaurantSubGroup = response?.data || {}

      setSelectedRestaurantSubGroup({
        ...restaurantSubGroup,
        restaurantGroupLabel: variables?.restaurantGroup?.name || '',
        restaurantGroupId: variables?.restaurantGroup?.id,
      })
    },
    onError: (error) => {
      setEditSubGroupModalOpen(false)
      setSelectedRestaurantSubGroup(null)
      showNotification(error?.response?.data?.error || 'Failed to load restaurant sub group details', 'error')
    }
  })

  const handleEditRestaurantSubGroup = async (subGroup, restaurantGroup) => {
    setEditSubGroupModalOpen(true)
    setIsFetchingSubGroupDetails(true)

    try {
      await fetchRestaurantSubGroupDetailsMutation({ subGroupId: subGroup.id, restaurantGroup })
    } catch {
      // Handled in onError
    } finally {
      setIsFetchingSubGroupDetails(false)
    }
  }

  const handleDrawerClose = () => {
    setOpen(false)
    setRestaurantGroupError('')
    setRestaurantGroup('')
    setCutOffDate(null)
    resetLogoState()
  }

  const handleEditDrawerClose = () => {
    setEditDrawerOpen(false)
    setRestaurantGroupError('')
    setRestaurantGroup('')
    setCutOffDate(null)
    resetLogoState()
  }

  const handleCutOffDateChange = (value) => {
    setCutOffDate(value)
    if (restaurantGroupError?.cut_off_date) {
      setRestaurantGroupError((previous) => ({ ...previous, cut_off_date: undefined }))
    }
  }

  const cutOffDateError = isInvalidCutOffDate(cutOffDate)
    ? 'Enter a valid date'
    : restaurantGroupError?.cut_off_date

  const handleEditSubGroupModalExited = React.useCallback(() => {
    setSelectedRestaurantSubGroup(null)
    setIsFetchingSubGroupDetails(false)
  }, [])

  const handleRestaurantGroup = (name) => {
    setRestaurantGroup(name);
    if (restaurantGroupError) {
      setRestaurantGroupError({});
    }
  };

  const handleLogoUpload = async (event, field) => {
    const file = event.target.files?.[0]

    if (!file) {
      return
    }

    if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
      setGroupLogos((previous) => ({
        ...previous,
        [field]: {
          ...previous[field],
          error: 'Please upload a PNG or JPEG file.',
        },
      }))
      showNotification('Please upload a PNG or JPEG file.', 'error')
      event.target.value = ''
      return
    }

    if (file.size > LOGO_MAX_SIZE_BYTES) {
      setGroupLogos((previous) => ({
        ...previous,
        [field]: {
          ...previous[field],
          error: 'File size must be 1 MB or smaller.',
        },
      }))
      showNotification('File size must be 1 MB or smaller.', 'error')
      event.target.value = ''
      return
    }

    const isValidDimensions = await new Promise((resolve, reject) => {
      const image = new Image()
      const objectUrl = URL.createObjectURL(file)

      image.onload = () => {
        URL.revokeObjectURL(objectUrl)
        resolve(image.width === LOGO_REQUIRED_WIDTH && image.height === LOGO_REQUIRED_HEIGHT)
      }

      image.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        reject(new Error('Failed to load image'))
      }

      image.src = objectUrl
    }).catch(() => false)

    if (!isValidDimensions) {
      const message = `The dimensions must be ${LOGO_REQUIRED_WIDTH} x ${LOGO_REQUIRED_HEIGHT} pixels.`
      setGroupLogos((previous) => ({
        ...previous,
        [field]: {
          ...previous[field],
          error: message,
        },
      }))
      showNotification(message, 'error')
      event.target.value = ''
      return
    }

    const base64Image = await convertBase64(file)

    setGroupLogos((previous) => ({
      ...previous,
      [field]: {
        url: base64Image,
        data: base64Image,
        error: '',
      },
    }))
    event.target.value = ''
  }

  const buildLogoPayload = () => {
    return Object.entries(groupLogos).reduce((payload, [field, logoState]) => {
      if (logoState.data?.startsWith('data:')) {
        payload[field] = logoState.data
      }

      return payload
    }, {
      wallet_gold_logo_backgrounds: goldLogoBackgrounds,
    })
  }

  const hasLogoValidationErrors = () => {
    return Object.values(groupLogos).some((logoState) => Boolean(logoState.error))
  }

  const handleGoldBackgroundInputChange = (value) => {
    setGoldBackgroundInput(normalizeHexColor(value))
    setGoldBackgroundError('')
  }

  const handleGoldBackgroundAdd = () => {
    const normalizedColor = normalizeHexColor(goldBackgroundInput)

    if (!HEX_COLOR_REGEX.test(normalizedColor)) {
      setGoldBackgroundError('Enter a valid 6-digit hex color such as #D4AF37.')
      return
    }

    if (goldLogoBackgrounds.includes(normalizedColor)) {
      setGoldBackgroundError(`${normalizedColor} is already in the list.`)
      return
    }

    if (goldLogoBackgrounds.length >= GOLD_BACKGROUND_LIMIT) {
      setGoldBackgroundError(`You can add up to ${GOLD_BACKGROUND_LIMIT} background colors.`)
      return
    }

    setGoldLogoBackgrounds((previous) => [...previous, normalizedColor])
    setSelectedGoldLogoBackground(normalizedColor)
    setGoldBackgroundInput(normalizedColor)
    setGoldBackgroundError('')
  }

  const handleGoldBackgroundRemove = (colorToRemove) => {
    if (goldLogoBackgrounds.length === 1) {
      return
    }

    const remainingColors = goldLogoBackgrounds.filter((color) => color !== colorToRemove)
    setGoldLogoBackgrounds(remainingColors)
    setGoldBackgroundError('')

    if (selectedGoldLogoBackground === colorToRemove) {
      const fallbackColor = remainingColors[0] || DEFAULT_GOLD_LOGO_BACKGROUNDS[0]
      setSelectedGoldLogoBackground(fallbackColor)
      setGoldBackgroundInput(fallbackColor)
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const dialogBody = ({ labelValue }) => {
    return (
      <>
        {'This action will permanently delete the'}
        <span style={{ fontWeight: "600" }}>
          {` '${labelValue}' `}
        </span>
      </>
    );
  };

  const { mutateAsync: createRestaurantGroupMutation } = useOtMutation({
    mutationFn: async (payload) => await api.post("/v1/admin/restaurant_groups", payload),
    invalidateKeys: [...restaurantGroupMutationKeyGroups.create],
    onSuccess: () => {
      setOpen(false)
      setStatus(false)
      showNotification('Restaurant Group has been created successfully', 'success')
      setRestaurantGroup('')
      setCutOffDate(null)
    },
    onError: (error) => {
      setRestaurantGroupError(error?.response?.data?.errors);
      setStatus(false)
    }
  });

  const handleAdd = async () => {
    if (hasLogoValidationErrors()) {
      showNotification('Please fix the logo validation errors before saving.', 'error')
      return
    }

    if (isInvalidCutOffDate(cutOffDate)) {
      return
    }

    setStatus(true);
    const payload = {
      name: restaurantGroup,
      ...buildLogoPayload(),
    }
    const formattedCutOffDate = formatCutOffDate(cutOffDate)
    if (canEditCutOffDate && formattedCutOffDate) {
      payload.cut_off_date = formattedCutOffDate
    }

    try {
      await createRestaurantGroupMutation(payload);
    } catch {
      // Handled in onError
    }
  };

  const { mutateAsync: updateRestaurantGroupMutation } = useOtMutation({
    mutationFn: async (payload) => await api.put(`/v1/admin/restaurant_groups/${selectedRestaurantGroup.id}`, payload),
    invalidateKeys: [...restaurantGroupMutationKeyGroups.update],
    onSuccess: () => {
      setEditDrawerOpen(false)
      setStatus(false)
      showNotification('Restaurant Group has been updated successfully', 'success')
      setRestaurantGroup('')
      setCutOffDate(null)
      setRestaurantGroupError('')
    },
    onError: (error) => {
      setRestaurantGroupError(error?.response?.data?.errors);
      setStatus(false)
    }
  });

  const handleUpdate = async () => {
    if (!restaurantGroupName) {
      setRestaurantGroupError({name: "Name can't be blank"})
      return
    }

    if (hasLogoValidationErrors()) {
      showNotification('Please fix the logo validation errors before saving.', 'error')
      return
    }

    if (isInvalidCutOffDate(cutOffDate)) {
      return
    }

    setStatus(true);
    const payload = {
      name: restaurantGroupName,
      ...buildLogoPayload(),
    }
    // Superadmins always send it so an emptied picker clears the stored date.
    if (canEditCutOffDate) {
      payload.cut_off_date = formatCutOffDate(cutOffDate)
    }

    try {
      await updateRestaurantGroupMutation(payload);
    } catch {
      // Handled in onError
    }
  };

  const { mutateAsync: deleteRestaurantGroupMutation } = useOtMutation({
    mutationFn: async () => await api.delete(`/v1/admin/restaurant_groups/${selectedRestaurantGroup.id}`),
    invalidateKeys: [...restaurantGroupMutationKeyGroups.delete],
    errorMessage: () => null,
    onSuccess: () => {
      setDialogOpen(false);
      setLoadingStatus(false);
      showNotification("Restaurant Group deleted successfully", "success");
      setSelectedRestaurantGroup(null);

      resetPageAfterDeletion({ totalCount: data?.pageCount, removed: 1, page, itemsPerPage, setPage })
    },
    onError: (error) => {
      showNotification(error?.response?.data?.error || 'Failed to delete restaurant group', "error");
      setDialogOpen(false);
      setLoadingStatus(false);
    }
  });

  const { mutateAsync: deleteRestaurantSubGroupMutation } = useOtMutation({
    mutationFn: async (subGroupId) => await api.delete(`/v1/admin/restaurant_sub_groups/${subGroupId}`),
    invalidateKeys: [...restaurantGroupMutationKeyGroups.subGroupDelete],
    errorMessage: () => null,
    onSuccess: () => {
      showNotification("Restaurant Sub Group deleted successfully", "success");
      setSelectedRestaurantSubGroup(null)
      setDeleteSubGroupModalOpen(false)
      setIsDeletingSubGroup(false)
    },
    onError: (error) => {
      showNotification(error?.response?.data?.error || 'Failed to delete restaurant sub group', "error");
      setIsDeletingSubGroup(false)
      setDeleteSubGroupModalOpen(false)
    }
  });

  const { mutateAsync: deletePartnershipMutation } = useOtMutation({
    mutationFn: async (partnershipId) => await api.delete(`/v1/admin/restaurant_group_partnerships/${partnershipId}`),
    invalidateKeys: [...restaurantGroupMutationKeyGroups.partnershipDelete],
    errorMessage: () => null,
    onSuccess: () => {
      showNotification("Partnership deleted successfully", "success");
      setSelectedPartnership(null)
      setDeletePartnershipDialogOpen(false)
      setIsDeletingPartnership(false)
    },
    onError: (error) => {
      showNotification(error?.response?.data?.error || 'Failed to delete partnership', "error");
      setDeletePartnershipDialogOpen(false)
      setIsDeletingPartnership(false)
    }
  });

  const handlePartnershipDelete = async (partnershipId) => {
    setIsDeletingPartnership(true)

    try {
      await deletePartnershipMutation(partnershipId);
    } catch {
      // Handled in onError
    }
  }

  const handleDelete = async () => {
    setLoadingStatus(true);

    try {
      await deleteRestaurantGroupMutation();
    } catch {
      // Handled in onError
    }
  }

  const handleSubGroupModalClose = () => {
    setSubGroupModalOpen(false);
    setSelectedRestaurantSubGroup(null)
  }

  const handleRestaurantSubGroupDelete = async (subGroupId) => {
    setIsDeletingSubGroup(true)
    try {
      await deleteRestaurantSubGroupMutation(subGroupId);
    } catch {
      // Handled in onError
    }
  }

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=restaurant_groups&`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `restaurant_group_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const handleSort = (nextSortBy, nextSortOrder) => {
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
    setPage(1)
  }

  const TableRows = (data) => {
    return data?.map((record) => {
      return {
        id: record?.id,
        name: record?.name,
        restaurant_sub_groups_count: {
          display: (
            <RenderRestaurantSubGroups
              data={record?.restaurant_sub_groups}
              canEdit={strictCanScope(ability, 'update', 'Restaurant_Group', `${record?.id}`)}
              onEdit={(subGroup) => handleEditRestaurantSubGroup(subGroup, record)}
              canDelete={superAdmin?.role === 'admin'}
              onDelete={(subGroup) => {
                setSelectedRestaurantSubGroup(subGroup)
                setDeleteSubGroupModalOpen(true)
              }}
            />
          )
        },
        created_at: formatDate(record?.created_at, record?.time_zone_abbr),
        partners_count: {
          display: (
            <RenderRestaurantPartners
              data={record?.partners}
              canEdit={superAdmin?.role === 'admin'}
              onEdit={(partner) => {
                setSelectedPartnership(partner)
                setEditPartnershipModalOpen(true)
              }}
              canDelete={superAdmin?.role === 'admin'}
              onDelete={(partner) => {
                setSelectedPartnership(partner)
                setDeletePartnershipDialogOpen(true)
              }}
            />
          )
        },
        actions: (
          <div style={{ display: 'flex', gap: '2px' }}>
            <Tooltip
              title='Add partners'
              arrow
            >
              <span>
                <IconButton
                  onClick={() => {
                    setPartnershipRestaurantGroup(record)
                    setPartnershipModalOpen(true)
                  }}
                  disabled={!(superAdmin?.role === 'admin')}
                  data-testid={`restaurant-group-add-partner-button-${record?.id}`}
                >
                  <HandshakeIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? "#e0e0e0" : "#707070",
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title='Edit'
              arrow
            >
              <span>
                <IconButton
                  onClick={() => handleEditRestaurantGroup(record)}
                  disabled={!strictCanScope(ability, 'update', 'Restaurant_Group', `${record?.id}`)}
                  data-testid={`restaurant-group-edit-button-${record?.id}`}
                >
                  <ModeEditIcon
                    sx={{
                      color: !strictCanScope(ability, 'update', 'Restaurant_Group', `${record?.id}`) ? "#e0e0e0" : "#707070",
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip
              title='Delete'
              arrow
            >
              <span>
                <IconButton
                  onClick={() => {
                    setDialogOpen(true)
                    setSelectedRestaurantGroup(record)
                  }}
                  disabled={!(superAdmin?.role === 'admin')}
                  data-testid={`restaurant-group-delete-button-${record?.id}`}
                >
                  <DeleteIcon
                    sx={{
                      color: !(superAdmin?.role === 'admin') ? "#e0e0e0" : "#707070",
                      cursor: "pointer",
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )
      };
    });
  };

  const rows = TableRows(data?.data);
  const editRestaurantSubGroupInitialValues = React.useMemo(() => ({
    name: selectedRestaurantSubGroup?.name || '',
    description: selectedRestaurantSubGroup?.description || '',
    restaurantGroupId: selectedRestaurantSubGroup?.restaurantGroupId,
    restaurantGroupLabel: selectedRestaurantSubGroup?.restaurantGroupLabel || '',
    displayProfilePhotoUrl: selectedRestaurantSubGroup?.display_profile_photo?.url || '',
    walletDarkLogoUrl: selectedRestaurantSubGroup?.wallet_dark_logo?.url || '',
    walletLightLogoUrl: selectedRestaurantSubGroup?.wallet_light_logo?.url || '',
  }), [selectedRestaurantSubGroup]);

  return (
    <RestaurantGroupContainer style={{ width: open || editDrawerOpen ? 'calc(100% - 470px)' : '100%' }}>
      <StyledBreadCrumbContainer>
        <Breadcrumbs>
          <NavigableBreadcrumbLink 
            LinkComponent={StyledLink} 
            label="Home" to="/admin/dashboard" 
          />
          <StyledLink color={'#515152'}>
            Restaurant Groups
          </StyledLink>
        </Breadcrumbs>
      </StyledBreadCrumbContainer>

      <StyledTitleContainer>
        <StyledTitle>Restaurant Groups</StyledTitle>
      </StyledTitleContainer>

      <Divider style={{ borderStyle: 'solid', borderWidth: '1px' }} />
      <div style={{ padding: '25px 0px', gap: "10px", display: 'flex', flexWrap: 'wrap' }}>
        <StyledCreateButton
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={() => {
            resetLogoState()
            setCutOffDate(null)
            setOpen(true)
          }}
          disabled={!createPermission}
        >
          Create Restaurant Group
        </StyledCreateButton>
        <StyledCreateButton
          variant="contained"
          startIcon={<AddIcon/>}
          onClick={() => {
            setSubGroupModalOpen(true)
          }}
          disabled={!createPermission}
        >
          Create Restaurant Sub Group
        </StyledCreateButton>
      </div>

      {
        isLoading ? (
          <Loader />
        ) : (!readPermission ? (
          <div></div>
        ) : (
          <RestaurantGroupTable
            data={rows}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={data?.pageCount}
            fetchingLoader={isLoading}
            showDownloadButton={true}
            disableDownloadButton={data?.data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        )
        )
      }

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => handleDialogClose()}
        Icon={CloseIcon}
        dialogTitle={'Are you sure you want to delete the restaurant group?'}
        dialogBody={dialogBody({ labelValue: selectedRestaurantGroup?.name })}
        confirmationText={'Yes, I want to delete the restaurant group'}
        handleConfirm={handleDelete}
        isLoading={loadingStatus}
      />

      {/* Create Restaurant Group Drawer*/}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            marginTop: 'calc(35px * 2)',
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <div style={{padding: '0px 15px'}}>
          <StyledCreateTitle>Create Restaurant Group</StyledCreateTitle>

          <StyledFormContainer>
            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Restaurant group name<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => handleRestaurantGroup(e.target.value)}
                    error={Boolean(restaurantGroupError?.name)}
                    helperText={restaurantGroupError?.name}
                value={restaurantGroup}
                inputProps={{ 'data-testid': 'create-restaurant-group-name-input' }}
              />
            </div>

            {canEditCutOffDate && (
              <CutOffDateField
                value={cutOffDate}
                onChange={handleCutOffDateChange}
                error={cutOffDateError}
                testId="create-restaurant-group-cut-off-date-input"
              />
            )}

            <LogoUploadSection
              logos={groupLogos}
              onUpload={handleLogoUpload}
              goldLogoBackgrounds={goldLogoBackgrounds}
              selectedGoldLogoBackground={selectedGoldLogoBackground}
              onSelectGoldBackground={setSelectedGoldLogoBackground}
              goldBackgroundInput={goldBackgroundInput}
              onGoldBackgroundInputChange={handleGoldBackgroundInputChange}
              onGoldBackgroundAdd={handleGoldBackgroundAdd}
              onGoldBackgroundRemove={handleGoldBackgroundRemove}
              goldBackgroundError={goldBackgroundError}
            />
          </StyledFormContainer>
        </div>

        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: drawerWidth,
            height: '70px',
            zIndex: 2,
            backgroundColor: '#fff',
          }}
          elevation={2}
        >
          <StyledButtonContainer>
            <StyledCancelButton
              onClick={handleDrawerClose}
              variant="outlined"
              data-testid="create-restaurant-group-cancel-button"
            >
              Cancel
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleAdd}
              variant='contained'
              disabled={status}
              data-testid="create-restaurant-group-submit-button"
            >
              {status ? <LoadingSpinner isLoading={true} /> : 'Create'}
            </StyledAddButton>
          </StyledButtonContainer>
        </Paper>
      </Drawer>

      {/* Edit Restaurant Group Drawer*/}
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            marginTop: 'calc(35px * 2)',
          },
        }}
        variant="persistent"
        anchor="right"
        open={editDrawerOpen}
      >
        <div style={{padding: '0px 15px'}}>
          <StyledCreateTitle>Edit Restaurant Group</StyledCreateTitle>

          <StyledFormContainer>
            <div style={{marginTop: '3%'}}>
              <StyledFormFieldText>Restaurant group name<span style={{color: 'red'}}>*</span></StyledFormFieldText>
              <StyledTextField
                fullWidth
                size="small"
                onChange={(e) => {
                  setRestaurantGroupName(e.target.value)
                  if (restaurantGroupError) {
                    setRestaurantGroupError({});
                  }
                }}
                error={Boolean(restaurantGroupError?.name)}
                helperText={restaurantGroupError?.name}
                value={restaurantGroupName}
                inputProps={{ 'data-testid': 'edit-restaurant-group-name-input' }}
              />
            </div>

            {canEditCutOffDate && (
              <CutOffDateField
                value={cutOffDate}
                onChange={handleCutOffDateChange}
                error={cutOffDateError}
                testId="edit-restaurant-group-cut-off-date-input"
              />
            )}

            <LogoUploadSection
              logos={groupLogos}
              onUpload={handleLogoUpload}
              goldLogoBackgrounds={goldLogoBackgrounds}
              selectedGoldLogoBackground={selectedGoldLogoBackground}
              onSelectGoldBackground={setSelectedGoldLogoBackground}
              goldBackgroundInput={goldBackgroundInput}
              onGoldBackgroundInputChange={handleGoldBackgroundInputChange}
              onGoldBackgroundAdd={handleGoldBackgroundAdd}
              onGoldBackgroundRemove={handleGoldBackgroundRemove}
              goldBackgroundError={goldBackgroundError}
            />
          </StyledFormContainer>
        </div>

        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            width: drawerWidth,
            height: '70px',
            zIndex: 2,
            backgroundColor: '#fff',
          }}
          elevation={2}
        >
          <StyledButtonContainer>
            <StyledCancelButton
              onClick={handleEditDrawerClose}
              variant="outlined"
              data-testid="edit-restaurant-group-cancel-button"
            >
              Cancel
            </StyledCancelButton>
            <StyledAddButton
              onClick={handleUpdate}
              variant='contained'
              disabled={status}
              data-testid="edit-restaurant-group-submit-button"
            >
              {status ? <LoadingSpinner isLoading={true} /> : 'Save'}
            </StyledAddButton>
          </StyledButtonContainer>
        </Paper>
      </Drawer>

      <CreateRestaurantSubGroupModal
        open={subGroupModalOpen}
        onClose={() => setSubGroupModalOpen(false)}
      />

      <EditRestaurantSubGroupModal
        open={editSubGroupModalOpen}
        onClose={() => {
          setEditSubGroupModalOpen(false)
        }}
        onExited={handleEditSubGroupModalExited}
        isLoading={editSubGroupModalOpen && (isFetchingSubGroupDetails || !selectedRestaurantSubGroup)}
        subGroupId={selectedRestaurantSubGroup?.id}
        initialValues={editRestaurantSubGroupInitialValues}
      />

      <CreatePartnershipModal
        open={partnershipModalOpen}
        onClose={() => setPartnershipModalOpen(false)}
        restaurantGroup={partnershipRestaurantGroup}
      />

      <EditPartnershipModal
        open={editPartnershipModalOpen}
        onClose={() => setEditPartnershipModalOpen(false)}
        partnershipId={selectedPartnership?.id}
      />

      <ConfirmationDialog
        open={deletePartnershipDialogOpen}
        onClose={() => {
          setDeletePartnershipDialogOpen(false)
          setSelectedPartnership(null)
        }}
        Icon={CloseIcon}
        dialogTitle="Are you sure you want to delete the partnership?"
        dialogBody={dialogBody({ labelValue: selectedPartnership?.name })}
        confirmationText="Yes, I want to delete the partnership"
        handleConfirm={() => handlePartnershipDelete(selectedPartnership?.id)}
        isLoading={isDeletingPartnership}
        dataTestId="partnership-delete-dialog"
      />

      <ConfirmationDialog
        open={deleteSubGroupModalOpen}
        onClose={() => {
          setDeleteSubGroupModalOpen(false)
          setSelectedRestaurantSubGroup(null)
        }}
        Icon={CloseIcon}
        dialogTitle="Are you sure you want to delete the restaurant sub group?"
        dialogBody={dialogBody({ labelValue: selectedRestaurantSubGroup?.name })}
        confirmationText="Yes, I want to delete the restaurant sub group"
        handleConfirm={() => handleRestaurantSubGroupDelete(selectedRestaurantSubGroup?.id)}
        isLoading={isDeletingSubGroup}
      />
    </RestaurantGroupContainer>
  )
}
